import type { MealDay, Meal } from '@meal-planner/shared';
import { MealChip } from './MealChip.js';
import { Icon } from './ui/Icon.js';
import { BLWCard, type BLWAllergenEntry } from './BLWCard.js';

interface Props {
  day: MealDay;
  isLoading: boolean;
  lockedMeals: Set<string>;
  onToggleLock: (date: string, mealType: string) => void;
  onRefreshDay: (date: string) => void;
  onMealClick: (meal: Meal) => void;
  showPickyTags: boolean;
  isRefreshingWeek: boolean;
  // Phase E
  showBLWCards?: boolean;
  familyMembers?: Array<{ label?: string | null; age: number }>;
  allergenLog?: BLWAllergenEntry[];
  // G2 Area 3 — set of recipeIds that were auto-swapped to fit budget
  budgetSwapRecipeIds?: Set<string>;
  // G2 Areas 4–5 — mark-as-cooked
  planId?: string;
  cookedMeals?: Array<{ date: string; mealType: string; cookedAt: string }>;
}

function DaySkeleton() {
  return (
    <div className="space-y-2 animate-pulse" aria-busy="true" aria-label="Refreshing meals…">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-[88px] rounded-lg" style={{ background: 'var(--surface-2)' }} />
      ))}
    </div>
  );
}

export function DayColumn({
  day,
  isLoading,
  lockedMeals,
  onToggleLock,
  onRefreshDay,
  onMealClick,
  showPickyTags,
  isRefreshingWeek,
  showBLWCards = false,
  familyMembers = [],
  allergenLog = [],
  budgetSwapRecipeIds,
  planId,
  cookedMeals,
}: Props) {
  const dateObj = new Date(day.date + 'T12:00:00');
  const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
  const dayNum = dateObj.getDate();

  const todayStr = new Date().toISOString().slice(0, 10);
  const isToday = day.date === todayStr;
  const isPastDay = day.date < todayStr;

  const allLocked = day.meals.length > 0 && day.meals.every(
    (m) => lockedMeals.has(`${day.date}:${m.type}`),
  );

  const spinning = isLoading || isRefreshingWeek;

  return (
    <div className="flex flex-col gap-2 min-w-0">
      <div
        className="rounded-lg px-2 py-3 text-center flex-shrink-0"
        style={{
          background: isToday ? 'var(--accent-soft)' : 'var(--surface-2)',
          border: `1px solid ${isToday ? 'transparent' : 'var(--line-soft)'}`,
        }}
      >
        <div
          className="text-[10px] font-semibold tracking-[0.16em] uppercase"
          style={{ color: isToday ? 'var(--accent)' : 'var(--ink-3)' }}
        >
          {dayName}
        </div>
        <div
          className="h-serif"
          style={{
            fontSize: 22,
            fontStyle: 'italic',
            lineHeight: 1.1,
            color: isToday ? 'var(--accent)' : 'var(--ink)',
          }}
        >
          {dayNum}
        </div>
      </div>

      {spinning ? (
        <DaySkeleton />
      ) : (
        <div className="space-y-2">
          {day.meals.map((meal) => {
            const lockKey = `${day.date}:${meal.type}`;
            const isCooked = cookedMeals?.some((m) => m.date === day.date && m.mealType === meal.type) ?? false;
            return (
              <div key={meal.type} className="flex flex-col gap-1.5">
                <MealChip
                  meal={meal}
                  isLocked={lockedMeals.has(lockKey)}
                  onToggleLock={() => onToggleLock(day.date, meal.type)}
                  onClick={() => onMealClick(meal)}
                  showPickyTag={showPickyTags}
                  isBudgetSwap={!!(meal.recipeId && budgetSwapRecipeIds?.has(meal.recipeId))}
                  planId={planId}
                  mealDate={day.date}
                  isCooked={isCooked}
                />
                {showBLWCards && meal.type === 'dinner' && (
                  <BLWCard
                    meal={meal}
                    familyMembers={familyMembers}
                    allergenLog={allergenLog}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isPastDay && (
        <button
          onClick={() => onRefreshDay(day.date)}
          disabled={allLocked || spinning}
          aria-label={`Refresh ${dayName} meals`}
          title={allLocked ? 'All meals are locked' : `Refresh ${dayName}`}
          className="mt-auto inline-flex items-center justify-center gap-1.5 text-[11px] font-semibold py-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{
            background: allLocked ? 'transparent' : 'var(--surface-2)',
            color: allLocked ? 'var(--ink-4)' : 'var(--ink-2)',
            border: `1px solid var(--line-soft)`,
          }}
        >
          <Icon name="refresh" size={12} className={isLoading && !isRefreshingWeek ? 'animate-spin' : ''} />
          Refresh
        </button>
      )}
    </div>
  );
}
